import React, { useState } from 'react';
import { InfluencerProfile, TabId } from './InfluencerProfile';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, UserCircle, BarChart3, DollarSign, ChevronRight, IdCard, FileText } from 'lucide-react';
type MenuItem = {
  id: string;
  tab: TabId;
  section?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
};
export const InteractiveProfile = () => {
  const [activeTab, setActiveTab] = useState<TabId>('social');
  // Which region of the Social Media tab is spotlighted.
  const [activeSection, setActiveSection] = useState<string | null>('agency');
  // Which left-menu item is expanded.
  const [openItem, setOpenItem] = useState<string>('agency');
  const items: MenuItem[] = [{
    id: 'agency',
    tab: 'social',
    section: 'agency',
    icon: <Building2 size={18} data-id="element-1040" data-mp="fk3edzyhssqc" />,
    title: 'Agency details',
    description: "The talent agency manages this creator's profile — they input the specific talent manager's name, direct email, and phone number, and set whether the talent is exclusive or non-exclusive. This is how you reach out."
  }, {
    id: 'influencer',
    tab: 'social',
    section: 'influencer',
    icon: <UserCircle size={18} data-id="element-1041" data-mp="9rp7nbtsmtfj" />,
    title: 'Creator data',
    description: 'Defined by the talent agency — the creator\u2019s name, social links, collaboration types, location, niche, and content-style tags. The photo is auto-pulled from their social media.'
  }, {
    id: 'analytics',
    tab: 'social',
    section: 'analytics',
    icon: <BarChart3 size={18} data-id="element-1042" data-mp="3n1d3khwp5xpv" />,
    title: 'Live analytics',
    badge: 'Live · from Zano',
    description: 'The only part Zano provides — real-time data across TikTok, Instagram, and YouTube: followers, average views, average likes, engagement rate, and audience demographics. For Instagram, we also surface city-level audience data.'
  }, {
    id: 'pricing',
    tab: 'social',
    section: 'pricing',
    icon: <DollarSign size={18} data-id="element-1043" data-mp="1zce59thmo134" />,
    title: 'Pricing',
    description: 'Defined by the talent agency — the rates and deliverables they set directly, whether price ranges or flat fees, plus their notes on revisions and lead times.'
  }, {
    id: 'personal',
    tab: 'personal',
    icon: <IdCard size={18} data-id="element-1044" data-mp="1vaj6avn65zsi" />,
    title: 'Personal information',
    description: 'Defined by the talent agency — a tab they maintain with more personal details like real name, languages, gender, age, and location (country, state, and city).'
  }, {
    id: 'notes',
    tab: 'notes',
    icon: <FileText size={18} data-id="element-1045" data-mp="2e3eq6xzk9ng5" />,
    title: 'Notes',
    description: 'Defined by the talent agency — an open bio the talent manager writes covering background, past collaborations, awards, and a general description. The keywords here power search when brands and agencies query the database.'
  }];
  const handleSelect = (item: MenuItem) => {
    setOpenItem(item.id);
    setActiveTab(item.tab);
    // Only the Social Media tab has spotlightable regions.
    setActiveSection(item.tab === 'social' ? item.section ?? null : null);
  };
  return <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start w-full" data-id="element-1046" data-mp="144vfxeyla8uu">
      {/* Left side: Explainer Menu */}
      <div className="w-full lg:w-1/3 flex flex-col gap-3 shrink-0" data-id="element-1047" data-mp="z47tl6lb2g6y">
        <div className="mb-4" data-id="element-1048" data-mp="bhj1xt6hv9d5">
          <h3 className="text-2xl font-bold text-zano-charcoal mb-2" data-id="element-1049" data-mp="bede0h9lqdj0">
            The anatomy of a profile
          </h3>
          <p className="text-sm text-gray-600" data-id="element-1050" data-mp="l69fm2awclrz">
            Click a section to see how Zano brings live social data together
            with direct agency access.
          </p>
        </div>

        <div className="flex flex-col gap-2" data-id="element-1051" data-mp="3ovmeekudjpgv">
          {items.map(item => {
          const isOpen = openItem === item.id;
          return <div key={item.id} className={`rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer ${isOpen ? 'border-zano-pink bg-white shadow-md' : 'border-gray-200 bg-gray-50/50 hover:border-zano-pink/40 hover:bg-white'}`} onClick={() => handleSelect(item)} data-id="element-1052" data-mp="3jw03ii20qlb8">
                <div className="p-4 flex items-center justify-between" data-id="element-1053" data-mp="g2icdmlclbuu">
                  <div className="flex items-center gap-3" data-id="element-1054" data-mp="g4v42rgwr0l3">
                    <div className={`p-2 rounded-lg ${isOpen ? 'bg-zano-blush text-zano-pink' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1055" data-mp="k3c2mmm9j2qw">
                      {item.icon}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1056" data-mp="u0gmgfsmym4j">
                      <h4 className={`font-semibold ${isOpen ? 'text-zano-charcoal' : 'text-gray-700'}`} data-id="element-1057" data-mp="z0bfopzvvnsr">
                        {item.title}
                      </h4>
                      {item.badge && <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold" data-id="element-1058" data-mp="1hxmpvi1alg7k">
                          {item.badge}
                        </span>}
                    </div>
                  </div>
                  <ChevronRight size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-90 text-zano-pink' : 'text-gray-400'}`} data-id="element-1059" data-mp="146qd90nrh9m5" />
                </div>

                <AnimatePresence data-id="element-1060" data-mp="1ww725oc6a8dm">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1061" data-mp="1rvsq7v7gaeab">
                      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 mt-2 pt-3" data-id="element-1062" data-mp="hwrkvivjyrio">
                        {item.description}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Right side: Interactive Profile */}
      <div className="w-full lg:w-2/3 relative" data-id="element-1063" data-mp="3v5cw4yrq8plj">
        {/* Decorative background glow */}
        <div className="absolute -inset-10 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[3rem] blur-3xl -z-10" data-id="element-1064" data-mp="3he7q9exrn12r" />

        <div className="relative w-full max-w-2xl mx-auto" data-id="element-1065" data-mp="3lcf709s8c2dy">
          <InfluencerProfile activeSection={activeSection} activeTab={activeTab} onTabChange={tab => {
          setActiveTab(tab);
          // Sync the left menu when the user clicks tabs on the card itself.
          const match = items.find(i => i.tab === tab);
          if (tab !== 'social') {
            setActiveSection(null);
            if (match) setOpenItem(match.id);
          } else {
            setActiveSection('agency');
            setOpenItem('agency');
          }
        }} onSectionClick={section => {
          // When user clicks a section on the profile, sync the left menu
          const match = items.find(i => i.section === section);
          if (match) {
            handleSelect(match);
          }
        }} data-id="element-1066" data-mp="37gbhgtsawi7t" />
        </div>
      </div>
    </div>;
};